'use client';

import { useState, useEffect, useRef } from 'react';
import { getConsentRecord, ConsentRecord } from '@/lib/consent';

interface ConsentPreferenceCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (choices: ConsentRecord['choices']) => void;
}

export default function ConsentPreferenceCenter({
  isOpen,
  onClose,
  onSave,
}: ConsentPreferenceCenterProps) {
  const [choices, setChoices] = useState<ConsentRecord['choices']>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);

  // Load current consent on open
  useEffect(() => {
    if (isOpen) {
      const existing = getConsentRecord();
      if (existing) {
        setChoices(existing.choices);
      }
    }
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = dialogRef.current?.querySelectorAll(
          'button, [role="switch"]'
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const first = focusableElements[0] as HTMLElement;
        const last = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    firstFocusableRef.current?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleToggle = (category: keyof ConsentRecord['choices']) => {
    if (category === 'necessary') return; // Necessary is always on
    setChoices((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleAcceptAll = () => {
    const allChoices = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setChoices(allChoices);
    onSave(allChoices);
    onClose();
  };

  const handleSave = () => {
    onSave(choices);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-4 border-b border-slate-700">
            <h2
              id="preferences-title"
              className="text-xl font-bold text-white"
            >
              Cookie Preferences
            </h2>
            <p className="text-sm text-slate-300 mt-1">
              Customize your consent settings below
            </p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Necessary */}
            <div className="pb-4 border-b border-slate-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">Necessary</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Essential for site functionality, security, and fraud prevention. Always enabled.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked
                  disabled
                  className="ml-4 px-3 py-1 bg-emerald-600 text-white text-xs font-semibold rounded cursor-not-allowed opacity-75"
                >
                  ON
                </button>
              </div>
            </div>

            {/* Analytics */}
            <div className="pb-4 border-b border-slate-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">Analytics</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Help us understand how you use our site to improve your experience.
                  </p>
                </div>
                <button
                  ref={firstFocusableRef}
                  role="switch"
                  aria-checked={choices.analytics}
                  onClick={() => handleToggle('analytics')}
                  className={`ml-4 px-3 py-1 text-xs font-semibold rounded transition-colors ${
                    choices.analytics
                      ? 'bg-emerald-600 text-white' :'bg-slate-200 text-slate-700'
                  }`}
                >
                  {choices.analytics ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>

            {/* Marketing */}
            <div className="pb-4 border-b border-slate-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">Marketing</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Enable personalized ads and retargeting based on your interests.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={choices.marketing}
                  onClick={() => handleToggle('marketing')}
                  className={`ml-4 px-3 py-1 text-xs font-semibold rounded transition-colors ${
                    choices.marketing
                      ? 'bg-emerald-600 text-white' :'bg-slate-200 text-slate-700'
                  }`}
                >
                  {choices.marketing ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>

            {/* Preferences */}
            <div>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">Preferences</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Remember your settings and personalization choices.
                  </p>
                </div>
                <button
                  ref={lastFocusableRef}
                  role="switch"
                  aria-checked={choices.preferences}
                  onClick={() => handleToggle('preferences')}
                  className={`ml-4 px-3 py-1 text-xs font-semibold rounded transition-colors ${
                    choices.preferences
                      ? 'bg-emerald-600 text-white' :'bg-slate-200 text-slate-700'
                  }`}
                >
                  {choices.preferences ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-slate-50 px-6 py-4 border-t border-slate-200 flex gap-3">
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white font-semibold rounded-md hover:bg-emerald-700 transition-colors"
            >
              Save Preferences
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-4 py-2 bg-slate-900 text-white font-semibold rounded-md hover:bg-slate-800 transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
